import { Button, Icon, Input, InputWrapper } from "@components";
import { PromotionTargetTypeEnum, PromotionTypeEnum } from "@utilities/enums";
import { toIsoDate } from "@utilities/functions";
import { Category, Product, Promotion } from "@utilities/types";
import { FC, useCallback, useMemo, useState } from "react";

interface Props {
    promotion?: Promotion;
    categories?: Category[];
    products?: Product[];
    onCancel: () => void;
    onCreatePromotion: (promotion: Partial<Promotion>) => void;
}

export const AddPromotion: FC<Props> = ({
    promotion,
    categories = [],
    products = [],
    onCancel,
    onCreatePromotion,
}) => {

    const [name, setName] = useState<string>(promotion?.name || "");
    const [type, setType] = useState<PromotionTypeEnum>(promotion?.type || PromotionTypeEnum.PERCENTAGE);
    const [value, setValue] = useState<number>(promotion?.value! || 0);
    const [startDate, setStartDate] = useState<string>(promotion?.startDate ? toIsoDate(promotion.startDate) : "");
    const [endDate, setEndDate] = useState<string>(promotion?.endDate ? toIsoDate(promotion.endDate) : "");
    const [note, setNote] = useState<string>(promotion?.note || "");
    const [isSingleUse, setIsSingleUse] = useState<boolean>(promotion?.isSingleUse ?? false); // Multi-usage par défaut pour campagnes

    // États pour le ciblage de la campagne
    const [targetType, setTargetType] = useState<PromotionTargetTypeEnum>(
        promotion?.targetType || PromotionTargetTypeEnum.ALL // Défaut: tous les produits pour campagne
    );
    const [targetCategories, setTargetCategories] = useState<string[]>(
        promotion?.targetCategories || []
    );
    const [targetProducts, setTargetProducts] = useState<string[]>(
        promotion?.targetProducts || []
    );

    const isFormValid = useMemo(() => {
        // Nom obligatoire pour toutes les campagnes
        const basicValid = name.trim() && type && value > 0 && startDate && endDate && new Date(endDate) >= new Date(startDate);

        // Validation selon le type de cible
        if (targetType === PromotionTargetTypeEnum.ALL) {
            return basicValid;
        }
        if (targetType === PromotionTargetTypeEnum.CATEGORIES) {
            return basicValid && targetCategories.length > 0;
        }
        if (targetType === PromotionTargetTypeEnum.PRODUCTS) {
            return basicValid && targetProducts.length > 0;
        }
        return basicValid;
    }, [type, value, startDate, endDate, targetType, name, targetCategories, targetProducts]);

    const handleCancel = useCallback(() => {
        setName("");
        setType(PromotionTypeEnum.PERCENTAGE);
        setValue(0);
        setStartDate("");
        setEndDate("");
        setNote("");
        setIsSingleUse(false);
        setTargetType(PromotionTargetTypeEnum.ALL);
        setTargetCategories([]);
        setTargetProducts([]);
        onCancel();
    }, [onCancel]);

    // Générer un code à partir du nom de la campagne (pour application automatique)
    const generateCodeFromName = useCallback((campaignName: string): string => {
        return campaignName
            .trim()
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Supprimer accents
            .replace(/[^A-Z0-9\s]/g, '') // Garder seulement lettres et chiffres
            .replace(/\s+/g, '_') // Remplacer espaces par _
            .substring(0, 30); // Limiter à 30 caractères
    }, []);

    const handleCreatePromotion = useCallback(async () => {
        if (!isFormValid) return;

        // Le code est généré automatiquement à partir du nom pour les campagnes
        const generatedCode = generateCodeFromName(name);

        onCreatePromotion({
            name: name.trim(),
            code: generatedCode, // Code auto-généré pour application automatique
            type,
            value,
            startDate: new Date(startDate),
            endDate: new Date(endDate),
            note,
            isSingleUse,
            targetType,
            targetCategories: targetType === PromotionTargetTypeEnum.CATEGORIES ? targetCategories : [],
            targetProducts: targetType === PromotionTargetTypeEnum.PRODUCTS ? targetProducts : [],
        });
        handleCancel();
    }, [
        name, type, value, startDate,
        endDate, note, isSingleUse, targetType,
        targetCategories, targetProducts, onCreatePromotion,
        handleCancel, isFormValid, generateCodeFromName,
    ]);

    const handleToggleCategory = useCallback((categoryId: string) => {
        setTargetCategories(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    }, []);

    const handleToggleProduct = useCallback((productId: string) => {
        setTargetProducts(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    }, []);

    // Prévisualisation de la remise
    const previewExample = useMemo(() => {
        if (!value || value <= 0) return null;
        const examplePrice = 10000;
        if (type === PromotionTypeEnum.PERCENTAGE) {
            const discount = examplePrice * (value / 100);
            return {
                original: examplePrice,
                discount,
                final: examplePrice - discount
            };
        }
        return {
            original: examplePrice,
            discount: value,
            final: Math.max(0, examplePrice - value)
        };
    }, [type, value]);

    return (
        <div className="flex flex-col gap-6 max-h-[70vh] overflow-y-auto">
            {/* Info box */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-start gap-2">
                <Icon iconName="Megaphone" size={18} className="text-purple-600 mt-0.5" />
                <div>
                    <p className="text-sm font-medium text-purple-800">Campagne promotionnelle</p>
                    <p className="text-xs text-purple-600">
                        La promotion s'applique automatiquement aux produits ciblés. Le prix barré et le prix promo seront visibles sur les cartes produit en boutique, et la réduction sera appliquée automatiquement dans le panier.
                    </p>
                </div>
            </div>

            {/* Section Nom de la campagne */}
            <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Icon iconName="Megaphone" size={16} />
                    Informations de la campagne
                </h4>
                <InputWrapper label="Nom de la campagne *">
                    <Input
                        placeholder="ex: Soldes d'été 2025"
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                </InputWrapper>
                <p className="text-xs text-gray-500 mt-1">
                    Le nom est utilisé pour identifier la campagne dans votre tableau de bord
                </p>
            </div>

            {/* Section Type de ciblage */}
            <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Icon iconName="Target" size={16} />
                    Ciblage de la promotion
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    <div
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${targetType === PromotionTargetTypeEnum.ALL
                            ? 'border-primary bg-primary/10'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        onClick={() => setTargetType(PromotionTargetTypeEnum.ALL)}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Icon iconName="Globe" size={18} className={targetType === PromotionTargetTypeEnum.ALL ? 'text-primary' : 'text-gray-500'} />
                            <span className="font-medium text-sm">Tous les produits</span>
                        </div>
                        <p className="text-xs text-gray-500">Appliquer à tout le catalogue</p>
                    </div>

                    <div
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${targetType === PromotionTargetTypeEnum.CATEGORIES
                            ? 'border-primary bg-primary/10'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        onClick={() => setTargetType(PromotionTargetTypeEnum.CATEGORIES)}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Icon iconName="FolderOpen" size={18} className={targetType === PromotionTargetTypeEnum.CATEGORIES ? 'text-primary' : 'text-gray-500'} />
                            <span className="font-medium text-sm">Par catégorie</span>
                        </div>
                        <p className="text-xs text-gray-500">Sélectionner des catégories</p>
                    </div>

                    <div
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${targetType === PromotionTargetTypeEnum.PRODUCTS
                            ? 'border-primary bg-primary/10'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        onClick={() => setTargetType(PromotionTargetTypeEnum.PRODUCTS)}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Icon iconName="Package" size={18} className={targetType === PromotionTargetTypeEnum.PRODUCTS ? 'text-primary' : 'text-gray-500'} />
                            <span className="font-medium text-sm">Produits spécifiques</span>
                        </div>
                        <p className="text-xs text-gray-500">Sélectionner des produits</p>
                    </div>
                </div>

                {/* Sélection des catégories */}
                {targetType === PromotionTargetTypeEnum.CATEGORIES && (
                    <div className="mt-3">
                        <label className="text-sm text-gray-600 mb-2 block">
                            Sélectionnez les catégories ({targetCategories.length} sélectionnée{targetCategories.length > 1 ? 's' : ''})
                        </label>
                        <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-white">
                            {categories.length > 0 ? (
                                categories.map(category => (
                                    <label
                                        key={category._id}
                                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm checkbox-primary"
                                            checked={targetCategories.includes(category._id!)}
                                            onChange={() => handleToggleCategory(category._id!)}
                                        />
                                        <span className="text-sm">{category.name}</span>
                                    </label>
                                ))
                            ) : (
                                <p className="text-sm text-gray-400 p-2">Aucune catégorie disponible</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Sélection des produits */}
                {targetType === PromotionTargetTypeEnum.PRODUCTS && (
                    <div className="mt-3">
                        <label className="text-sm text-gray-600 mb-2 block">
                            Sélectionnez les produits ({targetProducts.length} sélectionné{targetProducts.length > 1 ? 's' : ''})
                        </label>
                        <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-white">
                            {products.length > 0 ? (
                                products.map(product => (
                                    <label
                                        key={product._id}
                                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm checkbox-primary"
                                            checked={targetProducts.includes(product._id!)}
                                            onChange={() => handleToggleProduct(product._id!)}
                                        />
                                        <span className="text-sm">{product.name}</span>
                                    </label>
                                ))
                            ) : (
                                <p className="text-sm text-gray-400 p-2">Aucun produit disponible</p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Section Type et Valeur */}
            <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Icon iconName="Percent" size={16} />
                    Type et valeur de la remise
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputWrapper label="Type de la promotion">
                        <Input
                            type="select"
                            placeholder="Type de la promotion"
                            value={type}
                            onChange={e => setType(e.target.value as PromotionTypeEnum)}
                            options={[
                                { label: "Pourcentage (%)", value: PromotionTypeEnum.PERCENTAGE },
                                { label: "Montant fixe (Fcfa)", value: PromotionTypeEnum.FIXED },
                            ]}
                        />
                    </InputWrapper>
                    <InputWrapper label={`Valeur ${type === PromotionTypeEnum.PERCENTAGE ? "(%)" : "(Fcfa)"}`}>
                        <Input
                            type="number"
                            min={0}
                            max={type === PromotionTypeEnum.PERCENTAGE ? 100 : undefined}
                            placeholder={type === PromotionTypeEnum.PERCENTAGE ? "ex: 20" : "ex: 1000"}
                            value={value || ''}
                            onChange={e => setValue(Number(e.target.value))}
                        />
                    </InputWrapper>
                </div>

                {/* Prévisualisation */}
                {previewExample && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-500 mb-2">Exemple sur un article à 10 000 Fcfa :</p>
                        <div className="flex items-center gap-2 text-sm">
                            <span className="line-through text-gray-400">{previewExample.original.toLocaleString()} Fcfa</span>
                            <Icon iconName="ArrowRight" size={14} className="text-gray-400" />
                            <span className="font-bold text-green-600">{previewExample.final.toLocaleString()} Fcfa</span>
                            <span className="text-xs text-green-600">(-{previewExample.discount.toLocaleString()} Fcfa)</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Section Dates */}
            <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Icon iconName="Calendar" size={16} />
                    Période de validité
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputWrapper label="Date de début">
                        <Input
                            type="date"
                            placeholder="ex: 01/01/2025"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                        />
                    </InputWrapper>
                    <InputWrapper label="Date de fin">
                        <Input
                            type="date"
                            placeholder="ex: 01/01/2025"
                            min={startDate}
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                        />
                    </InputWrapper>
                </div>
                {startDate && endDate && new Date(endDate) < new Date(startDate) && (
                    <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                        <Icon iconName="AlertCircle" size={12} />
                        La date de fin doit être après la date de début
                    </p>
                )}
            </div>

            {/* Section Options */}
            <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Icon iconName="Settings" size={16} />
                    Options
                </h4>

                {/* Type d'usage */}
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200">
                    <input
                        id="singleUse"
                        type="checkbox"
                        className="checkbox checkbox-primary mt-0.5"
                        checked={isSingleUse}
                        onChange={(e) => setIsSingleUse(e.target.checked)}
                    />
                    <label htmlFor="singleUse" className="cursor-pointer">
                        <span className="text-sm font-medium text-gray-700">Usage unique</span>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {isSingleUse
                                ? "La promotion sera désactivée après sa première utilisation"
                                : "La promotion peut être utilisée plusieurs fois pendant sa période de validité"
                            }
                        </p>
                    </label>
                </div>

                {/* Note */}
                <div className="mt-4">
                    <InputWrapper label="Note interne (optionnel)">
                        <Input
                            type="textarea"
                            placeholder="ex: Promotion pour les fêtes de fin d'année"
                            value={note}
                            onChange={e => setNote(e.target.value)}
                        />
                    </InputWrapper>
                </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex justify-between pt-2 border-t sticky bottom-0 bg-white">
                <Button
                    variant="secondary"
                    onClick={handleCancel}
                >
                    Annuler
                </Button>

                <Button
                    variant="success"
                    onClick={handleCreatePromotion}
                    disabled={!isFormValid}
                >
                    <Icon iconName={promotion ? "Check" : "Plus"} size={16} className="mr-1" />
                    {promotion ? 'Mettre à jour' : 'Créer la campagne'}
                </Button>
            </div>
        </div>
    )
}
